'use client';

import { c as _c } from "react/compiler-runtime";
import type { AutoFixLoopToggleError } from '@/data/auto-fix-loop/use-auto-fix-loop-toggle';
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { CubicReviewActivity } from './pr-v2-auto-fix-loop-model';
import { describeAutoFixToggleRefusal, resolveAutoFixLine } from './pr-v2-auto-fix-loop-model';
import { AUTO_FIX_EXPLAINER, resolveAutoFixLoopSwitchState } from '@/components/auto-fix-loop/auto-fix-loop-switch';
import { useAutoFixLoopRetry } from '@/data/auto-fix-loop/use-auto-fix-loop-retry';
import { useAutoFixLoopStatus } from '@/data/auto-fix-loop/use-auto-fix-loop-status';
import { useAutoFixLoopToggle } from '@/data/auto-fix-loop/use-auto-fix-loop-toggle';
import { usePrV2CubicReviewSummary } from '@/data/v2/pr/use-pr-v2-cubic-review-summary';
import { describeAutoFixAllowance } from '@/lib/auto-fix-allowance/model';
import { AUTO_FIX_LOOP_ENABLE_QUERY_PARAM, AUTO_FIX_LOOP_ENABLE_QUERY_VALUE } from '@/lib/auto-fix-loop/links';

/**
 * Handles `?autoFix=enable` (the Slack DM's "Enable auto-fix" link, in DMs sent
 * before it opened the enable page instead) once the
 * status has loaded: drops the parameter so a reload does nothing, turns the
 * loop on through the switch, and confirms with a toast. It always asks the
 * API to enable, which is idempotent and decides eligibility itself, rather
 * than trusting a cached status that may be stale after client navigation.
 * It never turns the loop off.
 */
function useEnableAutoFixLoopFromUrl(data, toggle) {
  const $ = _c(4);
  const handledRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== data || $[1] !== toggle) {
    t0 = () => {
      if (handledRef.current || !data) {
        return;
      }
      const url = new URL(window.location.href);
      if (url.searchParams.get(AUTO_FIX_LOOP_ENABLE_QUERY_PARAM) !== AUTO_FIX_LOOP_ENABLE_QUERY_VALUE) {
        return;
      }
      handledRef.current = true;
      url.searchParams.delete(AUTO_FIX_LOOP_ENABLE_QUERY_PARAM);
      window.history.replaceState(window.history.state, "", url);
      toggle(true, "slack").then(_temp);
    };
    t1 = [data, toggle];
    $[0] = data;
    $[1] = toggle;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}

/**
 * Where an auto-fix line shows: the PR page's sidebar, or the PR tab in /code. Its toggles and run
 * links report which.
 */
function _temp(refusal) {
  if (refusal) {
    toast.error("Could not enable auto-fix", {
      description: refusal.message
    });
  } else {
    toast.success("Auto-fix enabled", {
      description: AUTO_FIX_EXPLAINER
    });
  }
}
export type PrV2AutoFixLoopSurface = 'pr_page' | 'code_pr_tab';

/**
 * What an auto-fix line shows and does, for a surface to lay out. `null` while the status loads,
 * and on a pull request that cannot run auto-fix and never did; once the loop has run, the line
 * stays with the switch disabled, and hovering the switch says why. The switch is optimistic and
 * stays live while a request runs.
 */
export function usePrV2AutoFixLoop(t0) {
  const $ = _c(26);
  const {
    owner,
    repo,
    number,
    cubicReview,
    surface
  } = t0;
  const {
    data,
    isPending,
    error
  } = useAutoFixLoopStatus(owner, repo, number);
  const toggle = useAutoFixLoopToggle(owner, repo, number);
  const {
    retry,
    isRetrying
  } = useAutoFixLoopRetry(owner, repo, number, surface);
  const cubicReviewSummary = usePrV2CubicReviewSummary(owner, repo, number);
  useEnableAutoFixLoopFromUrl(surface === "pr_page" ? data : undefined, toggle.toggle);
  if (isPending || error || !data) {
    return null;
  }
  let t1;
  if ($[0] !== data) {
    t1 = resolveAutoFixLoopSwitchState(data);
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const switchState = t1;
  if (!switchState) {
    return null;
  }
  const t2 = cubicReviewSummary.isPending ? undefined : cubicReviewSummary.data?.outstandingComments ?? 0;
  let t3;
  if ($[2] !== cubicReview || $[3] !== data || $[4] !== t2 || $[5] !== toggle.pendingTarget) {
    t3 = resolveAutoFixLine({
      data,
      pendingTarget: toggle.pendingTarget,
      cubicReview,
      openIssueCount: t2
    });
    $[2] = cubicReview;
    $[3] = data;
    $[4] = t2;
    $[5] = toggle.pendingTarget;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const resolution = t3;
  let t4;
  if ($[7] !== retry) {
    t4 = () => {
      retry().then(_temp2);
    };
    $[7] = retry;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleRetry = t4;
  let t5;
  if ($[9] !== data.allowance) {
    t5 = data.allowance ? describeAutoFixAllowance(data.allowance) : null;
    $[9] = data.allowance;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== toggle.error) {
    t6 = toggle.error ? describeAutoFixToggleRefusal(toggle.error) : null;
    $[11] = toggle.error;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== surface || $[14] !== toggle) {
    t7 = checked => void toggle.toggle(checked, surface);
    $[13] = surface;
    $[14] = toggle;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== data.noIssuesAt || $[17] !== handleRetry || $[18] !== isRetrying || $[19] !== resolution || $[20] !== surface || $[21] !== switchState || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = {
      surface,
      switchState,
      resolution,
      noIssuesAt: data.noIssuesAt,
      allowanceText: t5,
      refusal: t6,
      setEnabled: t7,
      retry: handleRetry,
      isRetrying
    };
    $[16] = data.noIssuesAt;
    $[17] = handleRetry;
    $[18] = isRetrying;
    $[19] = resolution;
    $[20] = surface;
    $[21] = switchState;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
function _temp2(result) {
  if (!result.ok) {
    toast.error("Could not retry auto-fix", {
      description: result.message
    });
  }
}
export type PrV2AutoFixLoop = NonNullable<ReturnType<typeof usePrV2AutoFixLoop>>;